import { useEffect, useMemo, useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { Loader2, LineChart as LineIcon, RefreshCw } from "lucide-react";
import {
  CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis, Legend,
} from "recharts";

// Drift + rollout monitoring for the helpdesk. Reads:
//   - helpdesk_eval_runs → nightly accuracy / citation / injection pass rates
//   - helpdesk_telemetry → per-variant retrieval confidence, latency, cost
//   - helpdesk_feedback  → per-variant CSAT
//   - helpdesk_tool_audit → per-variant error rate (rate_limit / denied / error)
// The variant filter lets an operator narrow every chart to the v1 (legacy)
// or v2 (new) rollout bucket to catch a bad staged rollout early.

type WindowKey = "14d" | "30d" | "90d";
const WINDOW_DAYS: Record<WindowKey, number> = { "14d": 14, "30d": 30, "90d": 90 };

type VariantKey = "all" | "v1" | "v2";
type Suite = "all" | "nightly" | "pr";

type EvalPoint = {
  day: string;
  accuracy: number | null;
  baseline: number | null;
  citation: number | null;
  injection: number | null;
};

type ConfPoint = { day: string; confidence: number | null; samples: number };

type VariantSummary = {
  variant: string;
  conversations: number;
  medianLatencyMs: number;
  avgTopSim: number;
  costUsd: number;
  csat: number | null;
  csatSamples: number;
  errorRate: number;
};

const COST_PER_M_IN = 0.1;
const COST_PER_M_OUT = 0.4;

function dayKey(d: string | Date) {
  const dt = typeof d === "string" ? new Date(d) : d;
  return dt.toISOString().slice(0, 10);
}
function median(xs: number[]) {
  if (!xs.length) return 0;
  const s = [...xs].sort((a, b) => a - b);
  const m = Math.floor(s.length / 2);
  return s.length % 2 ? s[m] : (s[m - 1] + s[m]) / 2;
}
function pct(n: number | null) { return n == null ? "—" : `${(n * 100).toFixed(1)}%`; }

export function HelpdeskTrendsPanel() {
  const [win, setWin] = useState<WindowKey>("30d");
  const [variant, setVariant] = useState<VariantKey>("all");
  const [suite, setSuite] = useState<Suite>("nightly");
  const [loading, setLoading] = useState(true);
  const [evalPoints, setEvalPoints] = useState<EvalPoint[]>([]);
  const [confPoints, setConfPoints] = useState<ConfPoint[]>([]);
  const [variantRows, setVariantRows] = useState<VariantSummary[]>([]);

  const load = async () => {
    setLoading(true);
    const days = WINDOW_DAYS[win];
    const since = new Date(Date.now() - days * 86400_000).toISOString();

    // 1. Nightly eval runs (server-side filter by suite; variant not stored per run).
    let evalQ = supabase
      .from("helpdesk_eval_runs")
      .select("created_at, accuracy, baseline_accuracy, citation_pass_rate, injection_pass_rate, suite")
      .gte("created_at", since)
      .order("created_at", { ascending: true })
      .limit(500);
    if (suite !== "all") evalQ = evalQ.eq("suite", suite);

    // 2. Telemetry for retrieval confidence + variant summary. retrieved_chunks
    // is a jsonb array of { id, title, similarity } — we compute top-1 client-side.
    let telQ = supabase
      .from("helpdesk_telemetry")
      .select("created_at, latency_ms, tokens_in, tokens_out, variant, retrieved_chunks, conversation_id")
      .gte("created_at", since)
      .limit(10000);
    if (variant !== "all") telQ = telQ.eq("variant", variant);

    // 3. Feedback for per-variant CSAT (join via conversation_id).
    const fbQ = supabase
      .from("helpdesk_feedback")
      .select("helpful, conversation_id, created_at")
      .gte("created_at", since)
      .limit(10000);

    // 4. Tool audit for error rate.
    const auditQ = supabase
      .from("helpdesk_tool_audit")
      .select("outcome, tool_name, conversation_id, created_at")
      .gte("created_at", since)
      .limit(20000);

    const [{ data: evalData }, { data: telData }, { data: fbData }, { data: auditData }] =
      await Promise.all([evalQ, telQ, fbQ, auditQ]);

    // ---- Eval chart: 1 point per day (average if multiple runs) ----
    const byDay = new Map<string, { acc: number[]; base: number[]; cit: number[]; inj: number[] }>();
    for (const r of (evalData ?? []) as Array<{
      created_at: string; accuracy: number; baseline_accuracy: number;
      citation_pass_rate: number | null; injection_pass_rate: number | null;
    }>) {
      const k = dayKey(r.created_at);
      const b = byDay.get(k) ?? { acc: [], base: [], cit: [], inj: [] };
      b.acc.push(Number(r.accuracy));
      b.base.push(Number(r.baseline_accuracy));
      if (r.citation_pass_rate != null) b.cit.push(Number(r.citation_pass_rate));
      if (r.injection_pass_rate != null) b.inj.push(Number(r.injection_pass_rate));
      byDay.set(k, b);
    }
    const evalArr: EvalPoint[] = [...byDay.entries()]
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([day, b]) => ({
        day,
        accuracy: b.acc.length ? b.acc.reduce((a, x) => a + x, 0) / b.acc.length : null,
        baseline: b.base.length ? b.base.reduce((a, x) => a + x, 0) / b.base.length : null,
        citation: b.cit.length ? b.cit.reduce((a, x) => a + x, 0) / b.cit.length : null,
        injection: b.inj.length ? b.inj.reduce((a, x) => a + x, 0) / b.inj.length : null,
      }));
    setEvalPoints(evalArr);

    // ---- Retrieval confidence trend (daily avg top-1 similarity) ----
    const telRows = (telData ?? []) as Array<{
      created_at: string; latency_ms: number | null;
      tokens_in: number | null; tokens_out: number | null;
      variant: string | null; retrieved_chunks: unknown; conversation_id: string | null;
    }>;
    const confByDay = new Map<string, number[]>();
    for (const r of telRows) {
      const chunks = Array.isArray(r.retrieved_chunks) ? r.retrieved_chunks as Array<{ similarity?: number }> : [];
      const top = chunks.reduce((mx, c) => Math.max(mx, Number(c?.similarity ?? 0)), 0);
      if (top > 0) {
        const k = dayKey(r.created_at);
        const arr = confByDay.get(k) ?? [];
        arr.push(top);
        confByDay.set(k, arr);
      }
    }
    const confArr: ConfPoint[] = [...confByDay.entries()]
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([day, arr]) => ({
        day,
        confidence: arr.reduce((a, x) => a + x, 0) / arr.length,
        samples: arr.length,
      }));
    setConfPoints(confArr);

    // ---- Per-variant rollout summary ----
    const fb = (fbData ?? []) as Array<{ helpful: boolean; conversation_id: string | null }>;
    const audit = (auditData ?? []) as Array<{ outcome: string; conversation_id: string | null }>;
    const convVariant = new Map<string, string>();
    for (const r of telRows) if (r.conversation_id && r.variant) convVariant.set(r.conversation_id, r.variant);

    const perVariant = new Map<string, {
      conv: Set<string>; lat: number[]; sim: number[]; tin: number; tout: number;
      up: number; down: number; errors: number; total: number;
    }>();
    const bucket = (v: string) => {
      let b = perVariant.get(v);
      if (!b) { b = { conv: new Set(), lat: [], sim: [], tin: 0, tout: 0, up: 0, down: 0, errors: 0, total: 0 }; perVariant.set(v, b); }
      return b;
    };
    for (const r of telRows) {
      const v = r.variant ?? "unknown";
      const b = bucket(v);
      if (r.conversation_id) b.conv.add(r.conversation_id);
      if (r.latency_ms) b.lat.push(r.latency_ms);
      const chunks = Array.isArray(r.retrieved_chunks) ? r.retrieved_chunks as Array<{ similarity?: number }> : [];
      const top = chunks.reduce((mx, c) => Math.max(mx, Number(c?.similarity ?? 0)), 0);
      if (top > 0) b.sim.push(top);
      b.tin += r.tokens_in ?? 0; b.tout += r.tokens_out ?? 0;
    }
    for (const r of fb) {
      const v = (r.conversation_id && convVariant.get(r.conversation_id)) || null;
      if (!v) continue;
      const b = bucket(v);
      if (r.helpful) b.up++; else b.down++;
    }
    for (const r of audit) {
      const v = (r.conversation_id && convVariant.get(r.conversation_id)) || null;
      if (!v) continue;
      const b = bucket(v);
      b.total++;
      if (r.outcome === "error" || r.outcome === "denied" || r.outcome === "rate_limited") b.errors++;
    }
    const summary: VariantSummary[] = [...perVariant.entries()]
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([v, b]) => {
        const csatSamples = b.up + b.down;
        return {
          variant: v,
          conversations: b.conv.size,
          medianLatencyMs: Math.round(median(b.lat)),
          avgTopSim: b.sim.length ? b.sim.reduce((a, x) => a + x, 0) / b.sim.length : 0,
          costUsd: (b.tin / 1_000_000) * COST_PER_M_IN + (b.tout / 1_000_000) * COST_PER_M_OUT,
          csat: csatSamples ? b.up / csatSamples : null,
          csatSamples,
          errorRate: b.total ? b.errors / b.total : 0,
        };
      });
    setVariantRows(summary);
    setLoading(false);
  };

  useEffect(() => { void load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [win, variant, suite]);

  const evalStatus = useMemo(() => {
    const last = evalPoints[evalPoints.length - 1];
    if (!last || last.accuracy == null || last.baseline == null) return null;
    return last.accuracy + 1e-9 >= last.baseline ? "on-target" : "below-baseline";
  }, [evalPoints]);

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between gap-4">
        <div>
          <CardTitle className="flex items-center gap-2">
            <LineIcon className="size-4 text-primary" />
            RAG drift & rollout trends
          </CardTitle>
          <CardDescription>
            Nightly eval accuracy, retrieval confidence over time, and per-variant health of the staged
            10→100% helpdesk rollout. Use the filters to drill into a specific bucket or suite.
          </CardDescription>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex rounded-md border border-border overflow-hidden">
            {(Object.keys(WINDOW_DAYS) as WindowKey[]).map((k) => (
              <button key={k} onClick={() => setWin(k)}
                className={`px-2.5 py-1 text-xs ${win === k ? "bg-primary text-primary-foreground" : "bg-background hover:bg-accent"}`}>
                {k}
              </button>
            ))}
          </div>
          <div className="flex rounded-md border border-border overflow-hidden">
            {(["all", "v1", "v2"] as VariantKey[]).map((k) => (
              <button key={k} onClick={() => setVariant(k)}
                className={`px-2.5 py-1 text-xs ${variant === k ? "bg-primary text-primary-foreground" : "bg-background hover:bg-accent"}`}>
                {k}
              </button>
            ))}
          </div>
          <div className="flex rounded-md border border-border overflow-hidden">
            {(["nightly", "pr", "all"] as Suite[]).map((k) => (
              <button key={k} onClick={() => setSuite(k)}
                className={`px-2.5 py-1 text-xs ${suite === k ? "bg-primary text-primary-foreground" : "bg-background hover:bg-accent"}`}>
                {k}
              </button>
            ))}
          </div>
          <Button variant="ghost" size="icon-sm" onClick={() => void load()} aria-label="Refresh">
            {loading ? <Loader2 className="size-4 animate-spin" /> : <RefreshCw className="size-4" />}
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        <section>
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium">Nightly eval accuracy vs baseline</h3>
            {evalStatus && (
              <Badge className={evalStatus === "on-target"
                ? "bg-primary/15 text-primary hover:bg-primary/20"
                : ""} variant={evalStatus === "on-target" ? "default" : "destructive"}>
                {evalStatus === "on-target" ? "on target" : "below baseline"}
              </Badge>
            )}
          </div>
          <div className="h-56">
            {evalPoints.length ? (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={evalPoints} margin={{ top: 4, right: 12, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
                  <XAxis dataKey="day" tick={{ fontSize: 11 }} />
                  <YAxis domain={[0, 1]} tickFormatter={(v) => `${Math.round(v * 100)}%`} tick={{ fontSize: 11 }} />
                  <Tooltip formatter={(v: number) => (v == null ? "—" : `${(v * 100).toFixed(1)}%`)} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Line type="monotone" dataKey="accuracy" stroke="hsl(var(--primary))" strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="baseline" stroke="hsl(var(--muted-foreground))" strokeDasharray="4 4" dot={false} />
                  <Line type="monotone" dataKey="citation" stroke="hsl(var(--chart-3, 190 90% 45%))" strokeWidth={1.5} dot={false} />
                  <Line type="monotone" dataKey="injection" stroke="hsl(var(--destructive))" strokeWidth={1.5} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-full grid place-items-center text-xs text-muted-foreground">
                No eval runs recorded in this window yet.
              </div>
            )}
          </div>
        </section>

        <section>
          <h3 className="text-sm font-medium mb-2">Retrieval confidence (avg top-1 similarity)</h3>
          <div className="h-48">
            {confPoints.length ? (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={confPoints} margin={{ top: 4, right: 12, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
                  <XAxis dataKey="day" tick={{ fontSize: 11 }} />
                  <YAxis domain={[0.5, 1]} tickFormatter={(v) => v.toFixed(2)} tick={{ fontSize: 11 }} />
                  <Tooltip formatter={(v: number, k) => k === "confidence" ? v.toFixed(3) : v} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Line type="monotone" dataKey="confidence" stroke="hsl(var(--primary))" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-full grid place-items-center text-xs text-muted-foreground">
                No retrieval telemetry in this window.
              </div>
            )}
          </div>
          <p className="text-[11px] text-muted-foreground mt-1">
            Threshold for the "I don't know" fallback is 0.72 — sustained dips below that indicate KB drift.
          </p>
        </section>

        <section>
          <h3 className="text-sm font-medium mb-2">Rollout health by variant</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase text-muted-foreground border-b border-border">
                  <th className="py-2 pr-4 font-medium">Variant</th>
                  <th className="py-2 pr-4 font-medium">Convs</th>
                  <th className="py-2 pr-4 font-medium">CSAT</th>
                  <th className="py-2 pr-4 font-medium">Median latency</th>
                  <th className="py-2 pr-4 font-medium">Avg retrieval</th>
                  <th className="py-2 pr-4 font-medium">Error rate</th>
                  <th className="py-2 font-medium">Cost</th>
                </tr>
              </thead>
              <tbody>
                {variantRows.length ? variantRows.map((r) => (
                  <tr key={r.variant} className="border-b border-border/50 last:border-0">
                    <td className="py-2 pr-4"><Badge variant="secondary">{r.variant}</Badge></td>
                    <td className="py-2 pr-4 font-mono tabular-nums">{r.conversations}</td>
                    <td className="py-2 pr-4 font-mono tabular-nums">{pct(r.csat)} <span className="text-[10px] text-muted-foreground">({r.csatSamples})</span></td>
                    <td className="py-2 pr-4 font-mono tabular-nums">{r.medianLatencyMs ? `${r.medianLatencyMs}ms` : "—"}</td>
                    <td className="py-2 pr-4 font-mono tabular-nums">{r.avgTopSim ? r.avgTopSim.toFixed(3) : "—"}</td>
                    <td className="py-2 pr-4 font-mono tabular-nums">
                      <span className={r.errorRate > 0.05 ? "text-destructive" : ""}>{pct(r.errorRate)}</span>
                    </td>
                    <td className="py-2 font-mono tabular-nums">${r.costUsd.toFixed(4)}</td>
                  </tr>
                )) : (
                  <tr><td colSpan={7} className="py-4 text-center text-xs text-muted-foreground">No variant data yet.</td></tr>
                )}
              </tbody>
            </table>
          </div>
          <p className="text-[11px] text-muted-foreground mt-2">
            A sustained CSAT delta &gt; 5pp or error-rate &gt; 5% on v2 should hold the staged rollout at its
            current percent — bump <code>helpdesk_rollout.percent</code> only after 24h of green metrics.
          </p>
        </section>
      </CardContent>
    </Card>
  );
}

export default HelpdeskTrendsPanel;
